// Types stricts du mode Agent autonome : contrat avec le backend Pegasux (/aurelia/autonomous/...).

export type AppMode = 'classic' | 'agent';

// ── Run ──────────────────────────────────────────────────────────────────────

export type RunStatus =
    | 'idle'        // inactif
    | 'running'     // en cours
    | 'stopping'    // arrêt demandé, en attente de confirmation
    | 'completed'   // terminé : quota atteint
    | 'stopped'     // arrêté manuellement
    | 'error';      // erreur

/** Option d'arrêt : deux choix exclusifs */
export type StopOption =
    | { stopMode: 'manual' }
    | { stopMode: 'count'; maxEmails: number };

export type StopMode = StopOption['stopMode'];

/**
 * Démarrage du scraping sur le desktop :
 * auto = le desktop démarre seul ; manual = la campagne attend le bouton « Démarrer » du desktop.
 */
export type DesktopStart = 'auto' | 'manual';

/** Options choisies au lancement, en plus de l'option d'arrêt */
export interface LaunchOptions {
    desktopStart: DesktopStart;
    /** Mode test : les emails partent vers l'utilisateur, pas vers les prospects */
    testMode: boolean;
    /** Lancement par URL : lien lu par Aurélia (recherche Sales Navigator, profil, entreprise, site, publication, offre) */
    sourceUrl?: string;
    /** Taille d'entreprise minimale (salariés) : la collecte ne descend jamais sous cette tranche ; 0 ou absent = toutes */
    minCompanySize?: number;
}

/** Corps de la requête « démarrer un run » */
export type StartRunRequest = StopOption & LaunchOptions;

export interface RunState {
    runId: string | null;
    status: RunStatus;
    /** Option d'arrêt du run (null tant qu'aucun run n'a été lancé) */
    stopOption: StopOption | null;
    /** Mode de démarrage du desktop choisi au lancement (null tant qu'aucun run n'a été lancé) */
    desktopStart: DesktopStart | null;
    /** Run lancé en mode test (null tant qu'aucun run n'a été lancé) */
    testMode: boolean | null;
    /** Entreprises analysées (validées ou non) */
    processedCompanies: number;
    /** Entreprises validées : celles qui correspondent à au moins une PV */
    validatedCompanies: number;
    /** Emails envoyés : c'est ce nombre qui est comparé à maxEmails */
    sentEmails: number;
    startedAt: string | null;
    endedAt: string | null;
    errorMessage: string | null;
    /** Persona mal alignée sur les offres : la plupart des premiers contacts sont écartés (conseil de ciblage) */
    targetingAlert?: string | null;
}

export const IDLE_RUN_STATE: RunState = {
    runId: null,
    status: 'idle',
    stopOption: null,
    desktopStart: null,
    testMode: null,
    processedCompanies: 0,
    validatedCompanies: 0,
    sentEmails: 0,
    startedAt: null,
    endedAt: null,
    errorMessage: null,
};

/** Un run est « actif » tant qu'il n'a pas reçu de confirmation d'arrêt ou de fin */
export const isRunActive = (status: RunStatus): boolean =>
    status === 'running' || status === 'stopping';

// ── Propositions de valeur (lecture seule) ───────────────────────────────────

export interface ValuePropositionTarget {
    job: string;
    activityDomain: string;
    commercialReasoning: string;
}

export interface ValuePropositionQA {
    question: string;
    answer: string;
}

export interface ValueProposition {
    /** Slug [a-z0-9_-]{1,64}, identique au header x-proposition-slug */
    slug: string;
    title: string;
    description: string;
    /** Contenu détaillé de la PV (« proposition-valeur.content ») */
    content: string;
    color: string;
    targets: ValuePropositionTarget[];
    qa: ValuePropositionQA[];
}

// ── Conditions d'éligibilité ─────────────────────────────────────────────────

export type EligibilityCategory =
    | 'activity_domain'
    | 'company_type'
    | 'company_size'
    | 'posts_content'
    | 'context'
    | 'other';

export interface EligibilityCondition {
    id: string;
    category: EligibilityCategory;
    /** L'exigence, rédigée en langage naturel */
    text: string;
}

export interface PvEligibility {
    pvSlug: string;
    conditions: EligibilityCondition[];
    updatedAt: string | null;
}

// ── Template des emails ──────────────────────────────────────────────────────

/** Mêmes contextes que le mode Classique et que template_body côté worker */
export type TemplateKey = 'A' | 'B' | 'C';

export interface TemplateContent {
    title: string;
    body: string;
}

export interface AutonomousEmailTemplate {
    /** A = post LinkedIn, B = profil, C = description de l'entreprise */
    templates: Record<TemplateKey, TemplateContent>;
    /** Signature commune, ajoutée à la fin de chaque email */
    signature: string;
    /**
     * PV pour laquelle le template a été rédigé. Le backend réécrit le template pour les
     * autres PV ; null = inconnue (le backend la déduit ou adapte le template à toutes les PV).
     */
    referencePvSlug: string | null;
    updatedAt: string | null;
}

export const EMPTY_AUTONOMOUS_TEMPLATE: AutonomousEmailTemplate = {
    templates: {
        A: { title: '', body: '' },
        B: { title: '', body: '' },
        C: { title: '', body: '' },
    },
    signature: '',
    referencePvSlug: null,
    updatedAt: null,
};

// ── Réglages du mode autonome ────────────────────────────────────────────────

export interface AutonomousSettings {
    /** Persona Sales Navigator (filtre « Persona » de la recherche de leads), nom saisi à la main */
    persona: { name: string } | null;
    updatedAt: string | null;
}

export const EMPTY_AUTONOMOUS_SETTINGS: AutonomousSettings = { persona: null, updatedAt: null };
